import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Card, Flex, Modal } from 'antd'
import CategorySelect from './CategorySelect'
import { useDispatch, useSelector } from 'react-redux'
import textSubmissionDrawerSelectors from '../../../../../../../../../../store/selectors/text-submission-drawer.selectors'
import assignmentSelectors from '../../../../../../../../../../store/selectors/assignment.selectors'
import { textSubmissionDrawerActions } from '../../../../../../../../../../store/slices/text-submission-drawer.slice'
import ReasoningInput from './ReasoningInput'
import FeedbackInput from './FeedbackInput'
import './grading-card.scss'
import { useParams } from 'react-router'
import { useExplanation } from '../../../../../../../../../../store/apis/utils/hooks/category.hooks'
import { useAnswerContext } from '../../../AnswerContext'
import { useFetchAssignmentSharedWithQuery } from '../../../../../../../../../../store/apis/shared-assignment.api'
import { fetchAssignmentSharedWithSelectors } from '../../../../../../../../../../store/apis/utils/selectors/shared-assignment.api.selectors'
import User from '../../../../../../../../../../components/common/user/User'
import { useAssignmentAccess } from '../../../../../../../access/Access'
import { Perm } from '../../../../../../../../../../constants/assignment-permissions'
import { useApprovalSpeedAnalytics } from '../../../../../../../../../../hooks/useApprovalSpeedAnalytics'
import { ASSIGNMENT_TYPES } from '../../../../../../../../../../constants/assignment-types'
import useRubricContext from '../../../../../../../../../../hooks/useRubricContext'
import { ExplainabilityStatuses } from '../../../../../../../../../../constants/explainability-statuses'
import FlagToggle from '../../../../../../../../sections/submissions/header-section/flag/FlagToggle'

const GradingCard = ({ className, answer, questionRubric }) => {
    const assignmentType = useSelector(assignmentSelectors.type)
    const isOwner = useSelector(assignmentSelectors.isOwner)
    const ownerId = useSelector(assignmentSelectors.ownerId)
    const ownerName = useSelector(assignmentSelectors.ownerName)
    const sendReasoningToStudents = useSelector(assignmentSelectors.sendReasoningToStudents)
    const dispatch = useDispatch()
    const focusedQuestionRubricId = useSelector(textSubmissionDrawerSelectors.focusedQuestionRubricId)
    const { token } = useParams()
    const { categoryIdToHighlight, setCategoryIdToHighlight } = useAnswerContext()

    const { categories } = useRubricContext({ questionRubricId: questionRubric?.id })

    const selectedCategory = useMemo(
        () => categories.find(c => c.id === categoryIdToHighlight),
        [categories, categoryIdToHighlight],
    )

    const { data: assignee } = useFetchAssignmentSharedWithQuery(
        { token },
        {
            selectFromResult: result => fetchAssignmentSharedWithSelectors.byUserId({ id: answer.assigneeId }, result),
        },
    )

    const { hasAccess: hasReviewingPermissions } = useAssignmentAccess([Perm.CAN_REVIEW], null, answer.assigneeId)

    const showDetails = questionRubric.id === focusedQuestionRubricId

    const { explanation } = useExplanation(answer)

    // Analytics: Track approval speed (isolated from business logic)
    const isCurrentlyFocused = focusedQuestionRubricId === questionRubric.id
    const calculateApprovalSpeed = useApprovalSpeedAnalytics(
        answer.id,
        questionRubric.id,
        answer.submissionId,
        isCurrentlyFocused,
    )

    const handleClick = () => {
        if (focusedQuestionRubricId === questionRubric.id) return
        dispatch(textSubmissionDrawerActions.setFocusedQuestionRubricId(questionRubric.id))
        setCategoryIdToHighlight(answer?.categoryId)
    }

    // Determine whose info to display (assignee if available, or owner if it's the owner's assignment)
    const displayAssignee = assignee
        ? { id: assignee.id, name: assignee.name }
        : isOwner && answer.assigneeId === ownerId
          ? { id: ownerId, name: ownerName }
          : null

    const isSelectedPatternSaved = !categoryIdToHighlight || categoryIdToHighlight === answer.categoryId
    const isDraftMode = !isSelectedPatternSaved

    const [draftPersonalized, setDraftPersonalized] = useState(null)
    const [draftCanned, setDraftCanned] = useState(null)
    const personalizedTemplateKeyRef = useRef(0)
    const [personalizedApplyDraft, setPersonalizedApplyDraft] = useState(null)
    const [currentPersonalizedValue, setCurrentPersonalizedValue] = useState('')

    useEffect(() => {
        setDraftPersonalized(null)
        setDraftCanned(null)
        setPersonalizedApplyDraft(null)
        setCurrentPersonalizedValue('')
    }, [categoryIdToHighlight])

    const handleDraftPersonalizedChange = useCallback(value => setDraftPersonalized(value), [])
    const handleDraftCannedChange = useCallback(value => setDraftCanned(value), [])

    const reasoningValue = isSelectedPatternSaved
        ? answer.personalizedFeedback ||
          (answer.isAdequate ? explanation?.reasoning || answer.explainabilityReasoning || '' : '')
        : explanation?.isAdequate
          ? explanation?.reasoning || ''
          : ''

    const feedbackValue = (isSelectedPatternSaved ? answer.cannedFeedback : null) || selectedCategory?.feedback || ''

    const isModelAssignment = assignmentType === ASSIGNMENT_TYPES.MODEL.value
    const baseReadonly = !hasReviewingPermissions || isModelAssignment

    // ReasoningInput is editable only when sendReasoningToStudents is true
    const reasoningReadonly = baseReadonly || !sendReasoningToStudents

    // FeedbackInput is editable only when sendReasoningToStudents is false
    const feedbackReadonly = baseReadonly || sendReasoningToStudents

    const personalizedFeedbackRequired = !reasoningReadonly

    // Capture whether PF is populated while the card is focused, so we can use it
    // after losing focus (when currentPersonalizedValue and explanation are both
    // reset by shared AnswerContext changes and can no longer be trusted).
    // Written inline (render phase) so it is captured before any effects run.
    // null = card has never been focused; the server-side fallback is used instead.
    const hasPFWhenFocusedRef = useRef(null)
    const prevAnswerIdRef = useRef(answer.id)
    if (prevAnswerIdRef.current !== answer.id) {
        prevAnswerIdRef.current = answer.id
        hasPFWhenFocusedRef.current = null
    }
    if (showDetails && personalizedFeedbackRequired) {
        hasPFWhenFocusedRef.current = currentPersonalizedValue.trim().length > 0
    }

    const hasUnsavedPersonalizedEdits =
        !reasoningReadonly &&
        !isDraftMode &&
        currentPersonalizedValue.trim() !== (reasoningValue || '').trim()

    const pendingPersonalizedFeedback = hasUnsavedPersonalizedEdits ? currentPersonalizedValue : null

    const isPersonalizedFeedbackMissing = personalizedFeedbackRequired && (
        showDetails
            ? currentPersonalizedValue.trim().length === 0
            : hasPFWhenFocusedRef.current === null
                // Never focused: fall back to server-persisted fields, gating
                // explainabilityReasoning behind isAdequate to match reasoningValue logic
                ? (answer.personalizedFeedback || (answer.isAdequate ? answer.explainabilityReasoning : '') || '').trim().length === 0
                // Previously focused: use what was captured then
                : !hasPFWhenFocusedRef.current
    )

    // Keep a local copy of what's currently in the textarea so we can warn before overwriting
    // even when changes are unsaved (i.e., not yet reflected in `reasoningValue`).
    useEffect(() => {
        setCurrentPersonalizedValue(reasoningValue || '')
    }, [reasoningValue])

    const handleUseCannedForPersonalized = useCallback(() => {
        const text = feedbackValue || ''
        const apply = () => {
            personalizedTemplateKeyRef.current += 1
            setPersonalizedApplyDraft({
                key: personalizedTemplateKeyRef.current,
                text,
            })
        }

        // Only warn when there is already personalized feedback to overwrite.
        if ((currentPersonalizedValue || '').trim().length > 0) {
            Modal.confirm({
                title: 'Replace personalized feedback?',
                content:
                    'This will replace the current text in the personalized feedback field with the rubric feedback. You can edit it before saving, or use the red X on that field to discard unsaved changes.',
                okText: 'Replace',
                cancelText: 'Cancel',
                onOk: apply,
            })
            return
        }

        apply()
    }, [feedbackValue, currentPersonalizedValue])

    const showUseForPersonalized =
        showDetails && !reasoningReadonly && feedbackReadonly && (feedbackValue || '').trim().length > 0

    return (
        <Card
            size='small'
            title={
                <Flex align='center' gap={4} style={{ whiteSpace: 'normal' }}>
                    <span>{questionRubric.name}</span>
                </Flex>
            }
            onClick={handleClick}
            className={`${className} grading-card ${focusedQuestionRubricId === questionRubric.id && 'focused-grading-card'}`}
            data-id={questionRubric.id}
            extra={
                <Flex align='center' gap={8}>
                    {isOwner && displayAssignee && (
                        <User size='small' name={displayAssignee.name || '...'} id={displayAssignee.id} />
                    )}
                    <FlagToggle
                        answerId={answer.id}
                        submissionId={answer.submissionId}
                        questionId={answer.questionId}
                        questionRubricId={questionRubric.id}
                        flagged={answer.escalated === ExplainabilityStatuses.MANUAL_UNRESOLVED}
                        existingNote={answer.note}
                    />
                </Flex>
            }
        >
            <Flex vertical>
                {showDetails && (
                    <ReasoningInput
                        submissionId={answer.submissionId}
                        answerId={answer.id}
                        initialValue={reasoningValue}
                        questionRubricId={questionRubric.id}
                        isDraftMode={isDraftMode}
                        readonly={reasoningReadonly}
                        onDraftChange={handleDraftPersonalizedChange}
                        applyDraft={personalizedApplyDraft}
                        onCurrentValueChange={setCurrentPersonalizedValue}
                    />
                )}
                <CategorySelect
                    key={answer.submissionId}
                    submissionId={answer.submissionId}
                    answer={answer}
                    questionRubric={questionRubric}
                    categories={categories}
                    label={null}
                    showDetails={showDetails}
                    readonly={!hasReviewingPermissions}
                    calculateApprovalSpeed={calculateApprovalSpeed}
                    draftPersonalized={draftPersonalized}
                    draftCanned={draftCanned}
                    pendingPersonalizedFeedback={pendingPersonalizedFeedback}
                    approveDisabled={isPersonalizedFeedbackMissing}
                />
                {showDetails && (
                    <FeedbackInput
                        submissionId={answer.submissionId}
                        answerId={answer.id}
                        feedback={feedbackValue}
                        readonly={feedbackReadonly}
                        questionRubricId={questionRubric.id}
                        isDraftMode={isDraftMode}
                        onDraftChange={handleDraftCannedChange}
                        showUseForPersonalized={showUseForPersonalized}
                        onUseForPersonalized={handleUseCannedForPersonalized}
                    />
                )}
            </Flex>
        </Card>
    )
}

export default GradingCard
